import Vue from "vue";
import { MutationTree, ActionTree, GetterTree } from "vuex";
import { TradeOrder, C2COrder } from "@foxone/api/src/models/market";
import utils from "@/utils";

const state = () => ({
  orderTabIndex: "instant-trade",
  instant: {
    data: [],
    hasNext: true
  },
  auto: {
    data: [],
    hasNext: true
  },
  fiat: {
    data: [],
    hasNext: true
  }
});

export type OrderState = {
  orderTabIndex: string;
  instant: {
    data: TradeOrder[];
    hasNext: boolean;
  };
  auto: {
    data: TradeOrder[];
    hasNext: boolean;
  };
  fiat: {
    data: C2COrder[];
    hasNext: boolean;
  };
};

const getters: GetterTree<OrderState, {}> = {
  getGroupedInstantOrders(state) {
    return state.instant.data.reduce((group, order) => {
      const date = utils.date.formatDate(
        +order.created_at * 1000,
        "YYYY/MM/DD"
      );
      if (!group[date]) {
        group[date] = [];
      }
      group[date].push(order);
      return group;
    }, {});
  },
  getGroupedAutoOrders(state) {
    return state.auto.data.reduce((group, order) => {
      const date = utils.date.formatDate(
        +order.created_at * 1000,
        "YYYY/MM/DD"
      );
      if (!group[date]) {
        group[date] = [];
      }
      group[date].push(order);
      return group;
    }, {});
  },
  getGroupedFiatOrders(state) {
    return state.fiat.data.reduce((group, order) => {
      const date = utils.date.formatDate(
        +order.created_at * 1000,
        "YYYY/MM/DD"
      );
      if (!group[date]) {
        group[date] = [];
      }
      group[date].push(order);
      return group;
    }, {});
  }
};

const mutations: MutationTree<OrderState> = {
  SET_ORDER_TAB_INDEX(state, data) {
    state.orderTabIndex = data;
  },
  SET_INSTANT_ORDERS(state, { data, hasNext }) {
    Vue.set(state.instant, "data", data);
    Vue.set(state.instant, "hasNext", hasNext);
  },
  REMOVE_INSTANT_ORDERS(state) {
    state.instant = {
      data: [],
      hasNext: true
    };
  },
  SET_AUTO_ORDERS(state, { data, hasNext }) {
    Vue.set(state.auto, "data", data);
    Vue.set(state.auto, "hasNext", hasNext);
  },
  REMOVE_AUTO_ORDERS(state) {
    state.auto = {
      data: [],
      hasNext: true
    };
  },
  SET_FAIT_ORDERS(state, { data, hasNext }) {
    Vue.set(state.fiat, "data", data);
    Vue.set(state.fiat, "hasNext", hasNext);
  },
  ROMOVE_FAIT_ORDERS(state) {
    state.fiat = {
      data: [],
      hasNext: true
    };
  }
};

const actions: ActionTree<OrderState, {}> = {
  async loadInstantOrders(
    { commit, state },
    { refresh, params } = { refresh: false, params: {} }
  ) {
    const offset = !refresh ? state.instant.data.length : 0;
    const orders = await this.$api.market.getTradeOrders({
      ...params,
      limit: 10,
      offset
    });
    const hasNext = orders.length === 10;
    commit("SET_INSTANT_ORDERS", {
      data: refresh ? orders : [...state.instant.data, ...orders],
      hasNext
    });
  },
  removeInstantOrders({ commit }) {
    commit("REMOVE_INSTANT_ORDERS");
  },
  async loadAutoOrders(
    { commit, state },
    { refresh, params } = { refresh: false, params: {} }
  ) {
    const offset = !refresh ? state.auto.data.length : 0;
    const orders = await this.$api.market.getTradeOrders({
      ...params,
      limit: 10,
      offset
    });
    const hasNext = orders.length === 10;
    commit("SET_AUTO_ORDERS", {
      data: refresh ? orders : [...state.auto.data, ...orders],
      hasNext
    });
  },
  removeAutoOrders({ commit }) {
    commit("REMOVE_AUTO_ORDERS");
  },
  async loadFiatOrders(
    { commit, state },
    { refresh, params } = { refresh: false, params: {} }
  ) {
    const offset = !refresh ? state.fiat.data.length : 0;
    const orders = await this.$api.market.getWyreOrders({
      ...params,
      limit: 10,
      offset
    });
    const hasNext = orders.length === 10;
    commit("SET_FAIT_ORDERS", {
      data: refresh ? orders : [...state.fiat.data, ...orders],
      hasNext
    });
  },
  removeFiatOrders({ commit }) {
    commit("ROMOVE_FAIT_ORDERS");
  }
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
